<template>
  <div id="container" class="welcome-container">
    <template v-if="!initializationError">
      <div class="welcome-header">
        <p class="welcome-title">{{ t('welcome.title') }}</p>
      </div>
      <div class="progress-bar">
        <div class="progress-bar-value"></div>
      </div>
    </template>
    <div class="load-error" v-else>
      <p class="title">{{ t('welcome.error.title') }}</p>
      <p class="body">
        {{ t('welcome.error.body') }}
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useAuthFlow } from '@/composables/useAuthFlow';
import RouteNames from '@/configuration/router/names.routes';
import { StorageKeys } from '@/models/storage-keys';
import navigationService from '@/services/NavigationService';
import { useAuthStore } from '@/stores/authStore';
import { AuthUtils } from '@/utils/auth.utils';
import { StorageUtils } from '@/utils/storage.utils';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

const initializationError = ref(false);

const { t } = useI18n();
const authStore = useAuthStore();
const { checkCurrentAuth, navigateAfterAuthentication } = useAuthFlow();
const pageLoadTimestamp = Date.now();
const minTime = 1500;

const initAuthentication = async () => {
  try {
    let navHistory = await StorageUtils.getStorageItem(StorageKeys.NAV_HISTORY);

    if (
      [RouteNames.SpainAuth, RouteNames.FranceAuth].some(
        (route) => navHistory && navHistory.endsWith(route)
      )
    ) {
      await StorageUtils.removeStorageItem(StorageKeys.NAV_HISTORY);
      navHistory = '';
    }

    const isAuth = await checkCurrentAuth();

    const elapsed = Date.now() - pageLoadTimestamp;
    if (elapsed < minTime) {
      await new Promise((resolve) => setTimeout(resolve, minTime - elapsed));
    }

    if (isAuth) {
      await navigateAfterAuthentication();
    } else {
      const targetRoute = AuthUtils.getLoginRouteName(authStore.authMethod);
      navigationService.navigateTo({ name: targetRoute });
    }
  } catch (ex) {
    console.error('Error during initialization:', ex);
    initializationError.value = true;
  }
};

initAuthentication();
</script>

<style scoped lang="scss">
#container {
  height: 100vh;
}

.welcome-container {
  display: flex;
  flex-direction: column;
  gap: $space-1;
  align-items: center;
  justify-content: center;
  padding: 0 $space-5;
  height: 100%;
}

.welcome-header {
  display: flex;
  flex-direction: column;
  gap: $space-1;
  align-items: center;
}

.welcome-title {
  font-size: $headline-m-font-size;
  font-weight: $fw-bold;
}

.progress-bar {
  height: 4px;
  background-color: $color-neutral-100;
  width: 100%;
  overflow: hidden;
  border-radius: $radius-4;
}

.progress-bar-value {
  width: 100%;
  height: 100%;
  background-color: $color-primary-500;
  animation: indeterminateAnimation 1s infinite linear;
  transform-origin: 0% 50%;
}

@keyframes indeterminateAnimation {
  0% {
    transform: translateX(0) scaleX(0);
  }
  40% {
    transform: translateX(0) scaleX(0.4);
  }
  100% {
    transform: translateX(100%) scaleX(0.5);
  }
}

.load-error {
  text-align: left;
  padding: $space-2;
  border-radius: $radius-3;
  border: 1px solid $color-error-900;
  background-color: $color-error-100;

  .title {
    font-size: $body-l-font-size;
    line-height: $body-l-line-height;
    font-weight: $fw-medium;
  }

  .body {
    font-size: $body-m-font-size;
    line-height: $body-m-line-height;
    font-weight: $fw-regular;
  }
}

@media (min-height: $small-screen-height) {
  .welcome-container {
    gap: $space-3;
    padding: 0 $space-7;
  }

  .welcome-header {
    gap: $space-2;
  }

  .welcome-title {
    font-size: $body-xl-font-size;
  }
}
</style>
